import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const RecentlyViewedContext = createContext();

const STORAGE_KEY = 'vav_recently_viewed';
const MAX_ITEMS = 8;

export const RecentlyViewedProvider = ({ children }) => {
  const [recentlyViewed, setRecentlyViewed] = useState([]);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setRecentlyViewed(Array.isArray(parsed) ? parsed : []);
      }
    } catch (err) {
      console.error('Error loading recently viewed:', err);
      setRecentlyViewed([]);
    }
  }, []);

  // Save to localStorage when changed
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(recentlyViewed));
    } catch (err) {
      console.error('Error saving recently viewed:', err);
    }
  }, [recentlyViewed]);

  // Add a product to recently viewed
  const addToRecentlyViewed = useCallback((product) => {
    if (!product || !product.slug) return;

    const item = {
      slug: product.slug,
      name: product.name,
      price: product.price,
      original_price: product.original_price,
      image: product.images?.[0] || '',
      discount_percent: product.discount_percent,
      viewedAt: new Date().toISOString()
    };

    setRecentlyViewed(prev => {
      const filtered = prev.filter(p => p.slug !== product.slug);
      return [item, ...filtered].slice(0, MAX_ITEMS);
    });
  }, []);

  const clearRecentlyViewed = useCallback(() => {
    setRecentlyViewed([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const removeFromRecentlyViewed = useCallback((slug) => {
    setRecentlyViewed(prev => prev.filter(p => p.slug !== slug));
  }, []);

  return (
    <RecentlyViewedContext.Provider value={{
      recentlyViewed,
      addToRecentlyViewed,
      clearRecentlyViewed,
      removeFromRecentlyViewed
    }}>
      {children}
    </RecentlyViewedContext.Provider>
  );
};

export const useRecentlyViewed = () => {
  const context = useContext(RecentlyViewedContext);
  if (!context) {
    throw new Error('useRecentlyViewed must be used within RecentlyViewedProvider');
  }
  return context;
};

export default RecentlyViewedContext;
